import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { AppContext } from "../../contexts/AppContext";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import * as d3 from "d3";
import { TrainingRun } from "../../services/TrainingRunsService";
import { getTotalSectionNutrition } from "../../utils/NutritionUtils";
import dayjs from "dayjs";

interface ChartDataPoint {
  date: Date;
  runName: string;
  // Actual values
  carbsPerHour: number;
  sodiumPerHour: number;
  caloriesPerHour: number;
  waterPerHour: number;
  caffeinePerHour: number;
  proteinPerHour: number;
  fatPerHour: number;
  performance: number;
  gutIssues: number;
  temperature: number;
  duration: number;
  // Normalized values (0-100)
  normalizedCarbsPerHour: number;
  normalizedSodiumPerHour: number;
  normalizedCaloriesPerHour: number;
  normalizedWaterPerHour: number;
  normalizedCaffeinePerHour: number;
  normalizedProteinPerHour: number;
  normalizedFatPerHour: number;
  normalizedPerformance: number;
  normalizedGutIssues: number;
  normalizedTemperature: number;
  normalizedDuration: number;
}

type MetricKey = 'carbsPerHour' | 'sodiumPerHour' | 'caloriesPerHour' | 'waterPerHour' |
  'caffeinePerHour' | 'proteinPerHour' | 'fatPerHour' | 'performance' | 'gutIssues' |
  'temperature' | 'duration';

interface MetricConfig {
  key: MetricKey;
  labelId: string;
  color: string;
  defaultVisible: boolean;
  maxValue: number; // for normalization
  unit: string;
  group: 'nutrition' | 'performance';
}

// Ordered for display: Row 1 (performance metrics), Row 2 (main nutrition), Row 3 (other nutrition)
const METRICS: MetricConfig[] = [
  // Row 1: Performance metrics
  { key: 'performance', labelId: 'trainingRun.performance', color: '#FF6F00', defaultVisible: true, maxValue: 10, unit: '/10', group: 'performance' },
  { key: 'gutIssues', labelId: 'trainingRun.gutIssues', color: '#D32F2F', defaultVisible: true, maxValue: 10, unit: '/10', group: 'performance' },
  { key: 'temperature', labelId: 'trainingRun.temperature', color: '#7B1FA2', defaultVisible: false, maxValue: 10, unit: '/10', group: 'performance' },
  { key: 'duration', labelId: 'trainingRun.duration', color: '#303F9F', defaultVisible: false, maxValue: 480, unit: 'min', group: 'performance' },
  // Row 2: Main nutrition metrics
  { key: 'carbsPerHour', labelId: 'trainingRun.carbsPerHour', color: '#1976D2', defaultVisible: true, maxValue: 100, unit: 'g', group: 'nutrition' },
  { key: 'sodiumPerHour', labelId: 'trainingRun.sodiumPerHour', color: '#0097A7', defaultVisible: true, maxValue: 1000, unit: 'mg', group: 'nutrition' },
  { key: 'waterPerHour', labelId: 'data.waterPerHour', color: '#00796B', defaultVisible: false, maxValue: 2, unit: 'L', group: 'nutrition' },
  // Row 3: Other nutrition metrics
  { key: 'caloriesPerHour', labelId: 'data.caloriesPerHour', color: '#FBC02D', defaultVisible: false, maxValue: 500, unit: 'kcal', group: 'nutrition' },
  { key: 'caffeinePerHour', labelId: 'data.caffeinePerHour', color: '#388E3C', defaultVisible: false, maxValue: 200, unit: 'mg', group: 'nutrition' },
  { key: 'proteinPerHour', labelId: 'data.proteinPerHour', color: '#5D4037', defaultVisible: false, maxValue: 30, unit: 'g', group: 'nutrition' },
  { key: 'fatPerHour', labelId: 'data.fatPerHour', color: '#C2185B', defaultVisible: false, maxValue: 30, unit: 'g', group: 'nutrition' },
];

interface TrainingRunsChartProps {
  trainingRuns: TrainingRun[];
}

const TrainingRunsChart: React.FC<TrainingRunsChartProps> = observer(({ trainingRuns }) => {
  const intl = useIntl();
  const { nutritionProductsService, userService } = React.useContext(AppContext);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricKey>>(
    new Set(METRICS.filter(m => m.defaultVisible).map(m => m.key))
  );

  const [width, setWidth] = useState(800);
  const [dimensions] = useState({
    height: 400,
    margin: { top: 40, right: 40, bottom: 60, left: 16 }
  });

  useEffect(() => {
    if (!userService.user?.id) {
      return;
    }
    nutritionProductsService.loadNutritionProducts(userService.user.id);
  }, [nutritionProductsService, userService.user?.id]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Calculate max duration from actual data
  const maxDuration = Math.max(...trainingRuns.map(r => r.time), 480);

  // Process training runs into chart data points
  const chartData: ChartDataPoint[] = trainingRuns.map(run => {
    const timeInHours = run.time / 60;
    const nutritionValues = getTotalSectionNutrition(
      run.nutrition || [],
      nutritionProductsService.nutritionProducts
    );

    const carbsPerHour = timeInHours ? nutritionValues.carbs / timeInHours : 0;
    const sodiumPerHour = timeInHours ? nutritionValues.sodium / timeInHours : 0;
    const caloriesPerHour = timeInHours ? nutritionValues.calories / timeInHours : 0;
    const waterPerHour = timeInHours ? nutritionValues.water / timeInHours : 0;
    const caffeinePerHour = timeInHours ? nutritionValues.caffeine / timeInHours : 0;
    const proteinPerHour = timeInHours ? nutritionValues.protein / timeInHours : 0;
    const fatPerHour = timeInHours ? nutritionValues.fat / timeInHours : 0;

    return {
      date: run.date,
      runName: run.name,
      carbsPerHour,
      sodiumPerHour,
      caloriesPerHour,
      waterPerHour,
      caffeinePerHour,
      proteinPerHour,
      fatPerHour,
      performance: run.performance,
      gutIssues: run.gutIssues,
      temperature: run.temperature,
      duration: run.time,
      // Normalize to 0-100 scale
      normalizedCarbsPerHour: (carbsPerHour / 100) * 100,
      normalizedSodiumPerHour: (sodiumPerHour / 1000) * 100,
      normalizedCaloriesPerHour: (caloriesPerHour / 500) * 100,
      normalizedWaterPerHour: (waterPerHour / 2) * 100,
      normalizedCaffeinePerHour: (caffeinePerHour / 200) * 100,
      normalizedProteinPerHour: (proteinPerHour / 30) * 100,
      normalizedFatPerHour: (fatPerHour / 30) * 100,
      normalizedPerformance: (run.performance / 10) * 100,
      normalizedGutIssues: (run.gutIssues / 10) * 100,
      normalizedTemperature: (run.temperature / 10) * 100,
      normalizedDuration: (run.time / maxDuration) * 100,
    };
  }).sort((a, b) => a.date.getTime() - b.date.getTime());

  const handleMetricToggle = (key: MetricKey) => {
    setVisibleMetrics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!svgRef.current || chartData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const chartWidth = width - dimensions.margin.left - dimensions.margin.right;
    const height = dimensions.height - dimensions.margin.top - dimensions.margin.bottom;

    // Create main group
    const g = svg.append("g")
      .attr("transform", `translate(${dimensions.margin.left},${dimensions.margin.top})`);

    // X scale (dates)
    const xScale = d3.scaleTime()
      .domain(d3.extent(chartData, d => d.date) as [Date, Date])
      .range([0, chartWidth]);

    // Y scale (normalized 0-100)
    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);

    // X axis
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(5).tickFormat(d => dayjs(d as Date).format('MMM D')))
      .style("font-size", "10px");

    // Grid lines only (no Y-axis labels since we have multiple scales)
    g.append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-chartWidth).tickFormat(() => ""))
      .style("stroke-opacity", 0.1)
      .select(".domain")
      .remove();

    // Line generator
    const line = d3.line<ChartDataPoint>()
      .defined(d => !isNaN((d as any)[`normalized${METRICS[0].key.charAt(0).toUpperCase() + METRICS[0].key.slice(1)}`]))
      .x(d => xScale(d.date))
      .curve(d3.curveMonotoneX);

    // Draw lines and points for each visible metric
    METRICS.forEach(metric => {
      if (!visibleMetrics.has(metric.key)) return;

      const normalizedKey = `normalized${metric.key.charAt(0).toUpperCase() + metric.key.slice(1)}` as keyof ChartDataPoint;

      // Filter out points with missing data
      const validData = chartData.filter(d => {
        const value = d[normalizedKey];
        return value !== null && value !== undefined && !isNaN(value as number);
      });

      if (validData.length === 0) return;

      // Draw line
      g.append("path")
        .datum(validData)
        .attr("fill", "none")
        .attr("stroke", metric.color)
        .attr("stroke-width", 2)
        .attr("d", line.y(d => yScale(d[normalizedKey] as number)));

      // Draw points
      g.selectAll(`.point-${metric.key}`)
        .data(validData)
        .join("circle")
        .attr("class", `point-${metric.key}`)
        .attr("cx", d => xScale(d.date))
        .attr("cy", d => yScale(d[normalizedKey] as number))
        .attr("r", 4)
        .attr("fill", metric.color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);
    });

    // Hover interaction
    const hoverLine = g.append("line")
      .attr("class", "hover-line")
      .attr("stroke", "#000")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "3,3")
      .style("opacity", 0);

    const hoverPoint = g.append("circle")
      .attr("class", "hover-point")
      .attr("r", 4)
      .attr("fill", "#000")
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("opacity", 0);

    const tooltip = g.append("g")
      .attr("class", "tooltip")
      .style("opacity", 0);

    const tooltipRect = tooltip.append("rect")
      .attr("fill", "#fff")
      .attr("stroke", "#000")
      .attr("pointer-events", "none");

    const tooltipText = tooltip.append("text")
      .attr("font-size", "10px")
      .attr("fill", "#000");

    const bisectDate = d3.bisector<ChartDataPoint, Date>(d => d.date).left;

    function resizeTextRect(text: any, rect: any) {
      if (!text || !text.node()) return;
      const bbox = text.node()!.getBBox();
      rect.attr("x", bbox.x - 10).attr("y", bbox.y - 5).attr("width", bbox.width + 15).attr("height", bbox.height + 10);
    }

    svg.append("rect")
      .attr("transform", `translate(${dimensions.margin.left},${dimensions.margin.top})`)
      .attr("width", chartWidth)
      .attr("height", height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mousemove", function(event) {
        const [xPos] = d3.pointer(event);
        const x0 = xScale.invert(xPos - dimensions.margin.left);
        const i = bisectDate(chartData, x0, 1);
        const d0 = chartData[i - 1];
        const d1 = chartData[i];
        const d = d1 && x0.getTime() - d0.date.getTime() > d1.date.getTime() - x0.getTime() ? d1 : d0;

        if (!d) return;

        hoverLine
          .attr("x1", xScale(d.date))
          .attr("x2", xScale(d.date))
          .attr("y1", 0)
          .attr("y2", height)
          .style("opacity", 1);

        // Find the first visible metric to position the hover point
        const firstVisibleMetric = METRICS.find(m => visibleMetrics.has(m.key));
        if (firstVisibleMetric) {
          const normalizedKey = `normalized${firstVisibleMetric.key.charAt(0).toUpperCase() + firstVisibleMetric.key.slice(1)}` as keyof ChartDataPoint;
          const yValue = d[normalizedKey] as number;
          if (yValue !== null && yValue !== undefined && !isNaN(yValue)) {
            hoverPoint
              .attr("cx", xScale(d.date))
              .attr("cy", yScale(yValue))
              .style("opacity", 1);
          }
        }

        // Build tooltip content with visible metrics only
        const visibleMetricsList = METRICS.filter(m => visibleMetrics.has(m.key));
        const tooltipLines: Array<{ text: string; color: string }> = [
          { text: d.runName, color: '' },
          { text: dayjs(d.date).format('MMM D, YYYY'), color: '' },
        ];

        visibleMetricsList.forEach(metric => {
          const value = d[metric.key];
          if (value !== null && value !== undefined && !isNaN(value as number)) {
            let formattedValue: string;
            if (metric.key === 'duration') {
              // Format duration as "XXh XXmin"
              const hours = Math.floor(value as number / 60);
              const minutes = Math.floor(value as number % 60);
              formattedValue = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
            } else {
              formattedValue = `${Math.round(value as number * 10) / 10}${metric.unit}`;
            }
            tooltipLines.push({
              text: `${intl.formatMessage({ id: metric.labelId })}: ${formattedValue}`,
              color: metric.color
            });
          }
        });

        const isLessThanHalf = xScale(d.date) < chartWidth / 2;
        const tooltipTextX = isLessThanHalf ? '1.25em' : '-1.25em';
        const tooltipTextAnchor = isLessThanHalf ? 'start' : 'end';

        tooltipText
          .attr("fill", "#000")
          .style("text-anchor", tooltipTextAnchor)
          .selectAll("tspan")
          .data(tooltipLines)
          .join("tspan")
          .attr("x", xScale(d.date))
          .attr("y", 10)
          .attr("dx", tooltipTextX)
          .attr("dy", (_, i) => `${i * 1.25}em`)
          .text(d => d.text);

        resizeTextRect(tooltipText, tooltipRect);

        const tspansPosition = tooltipText.selectAll("tspan").nodes().map((tspan) => ({
          x: (tspan as any).getBBox().x,
          y: (tspan as any).getBBox().y,
          height: (tspan as any).getBBox().height,
        }));

        // Remove old color indicators
        tooltip.selectAll('.color-indicator').remove();

        // Add color indicators for each metric (skip first 2 lines which are name and date)
        tooltipLines.slice(2).forEach((line, i) => {
          if (line.color) {
            tooltip.append('rect')
              .attr('class', 'color-indicator')
              .attr('fill', line.color)
              .attr('x', tspansPosition[i + 2].x - 7)
              .attr('y', tspansPosition[i + 2].y + tspansPosition[i + 2].height / 2 - 2)
              .attr('width', 4)
              .attr('height', 4)
              .attr('pointer-events', 'none');
          }
        });

        tooltip.style("opacity", 1);
      })
      .on("mouseleave", () => {
        hoverLine.style("opacity", 0);
        hoverPoint.style("opacity", 0);
        tooltip.style("opacity", 0);
      });

  }, [chartData, visibleMetrics, dimensions, width, intl, nutritionProductsService]);

  if (trainingRuns.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Legend with checkboxes - arranged in 3 rows, 4 columns (grid) */}
      <Box
        marginBottom={2}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1,
        }}
      >
        {METRICS.map(metric => (
          <FormControlLabel
            key={metric.key}
            control={
              <Checkbox
                checked={visibleMetrics.has(metric.key)}
                onChange={() => handleMetricToggle(metric.key)}
                size="small"
                sx={{
                  color: metric.color,
                  '&.Mui-checked': {
                    color: metric.color,
                  },
                }}
              />
            }
            label={
              <span style={{ fontSize: '12px' }}>
                {intl.formatMessage({ id: metric.labelId })}
              </span>
            }
            sx={{ margin: 0 }}
          />
        ))}
      </Box>

      {/* Chart */}
      <Box ref={containerRef} width="100%">
        <svg
          ref={svgRef}
          width={width}
          height={dimensions.height}
          style={{ maxWidth: "100%", overflow: "visible", display: "block" }}
        />
      </Box>
    </Box>
  );
});

export default TrainingRunsChart;
